import { Injectable } from '@angular/core';

import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs/';

import { CommunicationService } from './communication.service';
import { User } from '../models/user';
import { BasicError } from '../models/error/error';

@Injectable()

/**
 * This class works with the my/ Route of the api to get all datas that are necessary
 * for the current User. For example: own Profile, image, own studies etc.
 */
export class ProfileService {
  private myProfilePath: String = '/api/v1/my/profile';
  private currentUser: User = null; // Current user that is saved
  private currentUserThreshold = 0; // time when fetched user get's obsolete

  private tmpGetMyProfile: ReplaySubject<any> = null; // Temporarly setting the tmpGetMyProfile

  // Updating profile url
  private updateProfileUrl: String = '/api/v1/my/profile';

  // Deleting profile url
  private deleteProfileUrl: String = '/api/v1/my/profile';

  // Updating Password url
  private updatePasswordUrl: String = '/api/v1/my/profile/password';

  // Getting Role of current user
  private rolePath: String = '/api/v1/my/roles';
  private role: String[] = [];
  private roleThresholdTimer = 2 * 10 * 1000; // Threshold add 2 minutes to actual time
  private roleThreshold = 0;
  private roleReplay: Subject<any> = null;

  constructor(private communication: CommunicationService) { }

  /**
   * This function returns the profile of the current user (if the user is logged into the system).
   * If the User isn't logged into the system an error will be thrown.
   */
  public getMyProfile(refresh?: Boolean): ReplaySubject<any> {
    if (this.tmpGetMyProfile && this.tmpGetMyProfile !== null) {
      return this.tmpGetMyProfile;
    }

    // create basic replay object
    const profileReplay: ReplaySubject<any> = new ReplaySubject(1);

    // Check if user already fetched and no refresh is needed
    if (!refresh && this.currentUser !== null && this.currentUserThreshold > (new Date()).getTime() && this.currentUser instanceof User) {
      // A User was already fetched so the user doesn't have to be fetched again
      profileReplay.next(this.currentUser);
      return profileReplay;
    }

    this.tmpGetMyProfile = profileReplay;

    // do a request to the api and return the Observable with an User Object injected
    const request = this.communication.get(this.myProfilePath);
    request.subscribe(json => {
      // Error handling, if no data could be retrieved an error object will be returned, propagate it further to the ui
      if (json instanceof BasicError) {
        console.log('---ProfileService---: Failed to load ressource. Token:');
        console.log(this.communication.getAuthentication());
        profileReplay.next(json);
        this.tmpGetMyProfile = null;
        return;
      }

      // Create a user with given data
      const id: String = json.data.id;
      const name: String = (json.data.attributes.name) ? json.data.attributes.name : '';
      const firstname: String = (json.data.attributes.firstname) ? json.data.attributes.firstname : '';
      const lastname: String = (json.data.attributes.lastname) ? json.data.attributes.lastname : '';

      const user: User = new User(id, name, firstname, lastname);

      // Setting threshold of currentUser
      this.currentUser = user;
      this.currentUserThreshold = new Date().getTime() + (2 * 60 * 1000); // 2 Minutes Treshold

      // Getting other parameters into user object
      if (json.data.attributes.email) {
        user.setEmail(json.data.attributes.email);
      }

      /* if (json.data.attributes.sex) {
        user.setSex(json.data.attributes.sex);
      } */

      if (json.data.attributes.sex) {
        switch (json.data.attributes.sex) {
          case 1: user.setSex('männlich');
            break;
          case 2: user.setSex('weiblich');
            break;
          default: user.setSex('neutral');
        }
      }

      if (json.data.attributes.picture) {
        user.setPicture(json.data.attributes.picture);
      }

      if (json.data.attributes.settings) {
        user.setSettings(json.data.attributes.settings);
      }

      profileReplay.next(user);
      this.tmpGetMyProfile = null;
    });

    return profileReplay;
  }

  /**
   * This function updates the user data with the given updateUser object
   * The currentUser and the updateUser will be compared and only different values
   * will be updated.
   * @param currentUser currently user data
   * @param updateUser data to be updated
   */
  public updateMyProfile(currentUser: User, updateUser: User, extensionPic?: string): Subject<any> {
    const retReplay = new Subject();

    const payload = {
      'data': {
        'type': 'users',
        'attributes': {
        }
      }
    };

    // Compare data and add it to payload
    if (currentUser.getName() !== updateUser.getName()) {
      payload.data.attributes['name'] = updateUser.getName();
    }

    if (currentUser.getFirstName() !== updateUser.getFirstName()) {
      payload.data.attributes['firstname'] = updateUser.getFirstName();
    }

    if (currentUser.getLastName() !== updateUser.getLastName()) {
      payload.data.attributes['lastname'] = updateUser.getLastName();
    }

    if (currentUser.getSex() !== updateUser.getSex()) {
      switch (updateUser.getSex()) {
        case 'm': payload.data.attributes['sex'] = 1;
          break;
        case 'f': payload.data.attributes['sex'] = 2;
          break;
        case 'n': payload.data.attributes['sex'] = 9;
          break;
      }
    }

    if (currentUser.getPicture() !== updateUser.getPicture()) {
      payload.data.attributes['picture'] = { content: updateUser.getPicture() };
      payload.data.attributes['picture'].extension = extensionPic;
    }

    payload.data.attributes['settings'] = updateUser.getSettings();

    // Sending patch http request to api
    this.communication.patch(this.updateProfileUrl, payload).subscribe(data => {
      if (data instanceof BasicError) {
        console.log('--------ProfileServcie---------: Failed to update profile.');
        console.log(data);
        retReplay.next(false);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  public updateMyPassword(newPassword: String, newPasswordConf: String) {
    const retReplay = new Subject();

    const payload = {
      'data': {
        'type': 'users',
        'attributes': {
        }
      }
    };

    // Compare data and add it to payload
    if (newPassword !== '') {
      payload.data.attributes['password'] = newPassword;
    }

    if (newPasswordConf !== '') {
      payload.data.attributes['password_confirmation'] = newPasswordConf;
    }

    this.communication.patch(this.updatePasswordUrl, payload).subscribe(data => {
      if (data instanceof BasicError) {
        console.log('--------ProfileServcie---------: Failed to update password.');
        console.log(data);
        retReplay.next(false);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  /**
   * This function deletes the current user
   */
  public deleteMyProfile() {
    const retReplay = new Subject();

    // Sending delete http request to api
    this.communication.delete(this.deleteProfileUrl).subscribe(data => {
      if (data instanceof BasicError) {
        console.log('--------ProfileServcie---------: Failed to delete profile.');
        console.log(data);
        retReplay.next(data);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  /**
   * This function returns the roles of current user
   */
  public getMyRoles(): Subject<any> {
    if (this.roleReplay === null && this.roleThreshold < (new Date()).getTime()) {
      this.roleReplay = new Subject<any>();
    } else if (this.roleThreshold > (new Date()).getTime()) {
      // Cached data will be returned instead of fetching it again
      const tt = new ReplaySubject(1);
      tt.next(this.role);
      return tt;
    } else {
      return this.roleReplay;
    }

    // Call resource
    const request = this.communication.get(this.rolePath);
    request.subscribe(json => {
      if (json instanceof BasicError) {
        // Error occured while fetching role
        console.log('----------ProfileService--------: Error while fetching profile:' + json.msg);
        this.roleReplay.next(json);
        this.roleReplay = null;
        this.roleThreshold = 0;
        return;
      }

      // Getting Data for role
      this.role = []; // initialize roles

      for (let i = 0; i < json.data.length; i++) {
        if (json.data[i].id !== undefined) {
          this.role.push(json.data[i].id);
        }
      }

      // Setting Threshold
      this.roleThreshold = new Date().getTime() + this.roleThresholdTimer;
      this.roleReplay.next(this.role);
      this.roleReplay = null;
    });

    return this.roleReplay;
  }

}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/profile.service.ts